// View Pitches V2 — the filter panel's logic, separated from its markup so it
// can be tested as arithmetic rather than through the DOM.
//
// The filtering is CLIENT-SIDE over the pitches already loaded for the active
// tab. That is a deliberate limit, not an oversight: the list endpoint
// (GET /dashboard/club/tender-pitches/) accepts exactly ONE filter parameter,
// `free_agents_only`, and the split for this rebuild forbids inventing query
// parameters. So the star toggle keeps travelling to the server (it is the
// existing club-side Free-Agents filter, only moved into the panel) and every
// other control narrows the cards on screen.
//
// A value that is missing is NOT a match: filtering "Age 21 to 27" cannot
// honestly keep a card that has no age on it. Such a card is hidden while that
// range is set and comes back when it is cleared.

import {
  ageOf,
  experienceYearsOf,
  footOf,
  heightOf,
  isStaffTender,
  licenceOf,
  marketValueOf,
  nationalitiesOf,
} from "./pitchFields";

export const ANY = "Any";

export const EMPTY_FILTERS = {
  ageMin: "",
  ageMax: "",
  heightMin: "",
  heightMax: "",
  marketMin: "",
  marketMax: "",
  feeMin: "",
  feeMax: "",
  experienceMin: "",
  experienceMax: "",
  foot: ANY,
  nationality: ANY,
  licence: ANY,
};

const isBlank = (value) => value === "" || value === null || value === undefined;

const inRange = (value, min, max) => {
  const hasMin = !isBlank(min);
  const hasMax = !isBlank(max);
  if (!hasMin && !hasMax) return true;
  // Nothing to compare against — a card without the value cannot satisfy a
  // range, and pretending it does would put it back in a list the club just
  // narrowed on purpose.
  if (value === null || value === undefined || !Number.isFinite(Number(value))) {
    return false;
  }
  const numeric = Number(value);
  if (hasMin && numeric < Number(min)) return false;
  if (hasMax && numeric > Number(max)) return false;
  return true;
};

const feeValueOf = (pitch) => {
  const raw = pitch?.transfer_fee;
  if (raw === null || raw === undefined || raw === "") return null;
  const numeric = Number(raw);
  return Number.isFinite(numeric) ? numeric : null;
};

/** The nationality names present on the loaded cards — every citizenship (L-FE-1) — sorted, without repeats. */
export const nationalityOptions = (pitches = []) =>
  Array.from(
    new Set(
      pitches
        .flatMap((pitch) => nationalitiesOf(pitch).map((nat) => nat.name))
        .filter((name) => typeof name === "string" && name.trim() !== ""),
    ),
  ).sort((a, b) => a.localeCompare(b));

/**
 * The licences present on the loaded cards.
 *
 * Derived from the data rather than a hard-coded UEFA list on purpose: a fixed
 * enum silently drops every licence the list does not know about, and the
 * dropdown would then hide cards the club can see.
 */
export const licenceOptions = (pitches = []) =>
  Array.from(
    new Set(
      pitches
        .map((pitch) => licenceOf(pitch))
        .filter((value) => typeof value === "string" && value.trim() !== ""),
    ),
  ).sort((a, b) => a.localeCompare(b));

/** The feet present on the loaded cards, in the order a club reads them. */
export const footOptions = (pitches = []) => {
  const present = new Set(
    pitches.map((pitch) => footOf(pitch)).filter(Boolean),
  );
  return ["Right", "Left", "Both"].filter((value) => present.has(value));
};

export const applyPitchFilters = (pitches = [], filters = EMPTY_FILTERS, tender) => {
  const staff = isStaffTender(tender);
  return pitches.filter((pitch) => {
    if (!inRange(ageOf(pitch, tender), filters.ageMin, filters.ageMax)) return false;
    if (filters.nationality !== ANY) {
      // A player with two passports matches the filter for either country (L-FE-3).
      if (!nationalitiesOf(pitch).some((nat) => nat.name === filters.nationality)) return false;
    }
    if (staff) {
      if (
        !inRange(
          experienceYearsOf(pitch),
          filters.experienceMin,
          filters.experienceMax,
        )
      ) {
        return false;
      }
      if (filters.licence !== ANY && licenceOf(pitch) !== filters.licence) {
        return false;
      }
      return true;
    }
    if (!inRange(heightOf(pitch), filters.heightMin, filters.heightMax)) return false;
    if (!inRange(marketValueOf(pitch), filters.marketMin, filters.marketMax)) {
      return false;
    }
    if (!inRange(feeValueOf(pitch), filters.feeMin, filters.feeMax)) return false;
    if (filters.foot !== ANY && footOf(pitch) !== filters.foot) return false;
    return true;
  });
};

/**
 * How many filters are switched on, for the badge on the Filters button.
 *
 * A from/to pair counts once — the club set "Age", not "two ages". The
 * Free-Agents star counts too even though it filters server-side, because from
 * the club's side it is one of the controls in the same panel.
 */
export const countActiveFilters = (
  filters = EMPTY_FILTERS,
  { staff = false, freeAgentsOnly = false } = {},
) => {
  const pair = (min, max) => !isBlank(filters[min]) || !isBlank(filters[max]);
  const flags = [
    pair("ageMin", "ageMax"),
    filters.nationality !== ANY,
    freeAgentsOnly,
  ];
  if (staff) {
    flags.push(pair("experienceMin", "experienceMax"));
    flags.push(filters.licence !== ANY);
  } else {
    flags.push(pair("heightMin", "heightMax"));
    flags.push(pair("marketMin", "marketMax"));
    flags.push(pair("feeMin", "feeMax"));
    flags.push(filters.foot !== ANY);
  }
  return flags.filter(Boolean).length;
};

export const hasActiveFilters = (filters, options) =>
  countActiveFilters(filters, options) > 0;
